import React, { useMemo, useState } from 'react';

import EntityDropDownFilterContainerComponent from 'components/entityViewer/entitiesDropDownFilter/EntityDropDownFilterContainer';
import { useFacilityOptions } from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { FCTransferDirectionTabs } from 'components/warehouse/components/fcTransfers/FCTransferDirectionTabs';
import {
    buildFCTransferFilterConfigsWithDirection,
    DEFAULT_FC_TRANSFER_DIRECTION,
    FC_TRANSFER_DIRECTION,
    FILTER_QUERY_KEYS,
    mergeFCTransferListFiltersWithRequiredContext,
    normalizeFCTransferDirection,
} from 'components/warehouse/components/fcTransfers/FCTransferFilterConfig';

/** Stable default so `initialValues` is not a fresh `{}` every render (avoids spurious sync effects). */
const EMPTY_INITIAL_FILTER_VALUES = Object.freeze({});

/**
 * FC Transfers filter bar
 *
 * @param {Object} props
 * @param {import('components/warehouse/components/fcTransfers/FCTransferFilterConfig').FcTransferDirection} [props.direction] - Controlled tab direction.
 * @param {import('components/warehouse/components/fcTransfers/FCTransferFilterConfig').FcTransferDirection} [props.defaultDirection] - Uncontrolled initial direction.
 * @param {number | string} [props.currentFacilityId] - Logged-in facility id that must always be sent to API.
 * @param {(next: import('components/warehouse/components/fcTransfers/FCTransferFilterConfig').FcTransferDirection) => void} [props.onDirectionChange]
 * @param {Object} [props.initialValues={}] - Initial filter values (e.g. from URL).
 * @param {(filters: Object) => void} [props.onChange] - Applied filter values including required direction/facility context.
 * @param {string} [props.className]
 */
export function FCTransfersFilters({
    className = '',
    currentFacilityId,
    defaultDirection = DEFAULT_FC_TRANSFER_DIRECTION,
    direction: directionProp,
    initialValues = EMPTY_INITIAL_FILTER_VALUES,
    onChange,
    onDirectionChange,
}) {
    const [internalDirection, setInternalDirection] = useState(normalizeFCTransferDirection(defaultDirection));
    const isDirectionControlled = directionProp !== undefined;
    const direction = normalizeFCTransferDirection(isDirectionControlled ? directionProp : internalDirection);

    const { data: facilityOptions = [], isLoading: isLoadingFacilities } = useFacilityOptions();

    const effectiveFilterConfigs = useMemo(() => {
        const base = buildFCTransferFilterConfigsWithDirection(direction);
        return base.map((cfg) => {
            if (cfg.remoteOptions?.queryId === FILTER_QUERY_KEYS.FACILITIES) {
                return {
                    ...cfg,
                    options: facilityOptions,
                    menuProps: {
                        ...cfg.menuProps,
                        isLoading: isLoadingFacilities,
                    },
                };
            }
            return cfg;
        });
    }, [direction, facilityOptions, isLoadingFacilities]);

    const handleDirectionChange = (next) => {
        if (next !== FC_TRANSFER_DIRECTION.OUTBOUND && next !== FC_TRANSFER_DIRECTION.INBOUND) {
            return;
        }
        if (!isDirectionControlled) {
            setInternalDirection(next);
        }
        onDirectionChange?.(next);
        // Direction changes FC filter meaning (source vs destination); clear user-applied filters (parent owns URL state).
        const cleared = {};
        onChange?.(
            mergeFCTransferListFiltersWithRequiredContext(cleared, {
                direction: next,
                currentFacilityId,
            }),
        );
    };

    const handleFilterChange = (filterValues) => {
        onChange?.(
            mergeFCTransferListFiltersWithRequiredContext(filterValues, {
                direction,
                currentFacilityId,
            }),
        );
    };

    return (
        <div className={`flex flex-col gap-3 ${className}`.trim()}>
            <EntityDropDownFilterContainerComponent
                key={direction}
                title="FC Transfer Filters"
                filterConfigs={effectiveFilterConfigs}
                initialValues={initialValues}
                onChange={handleFilterChange}
                autoApply
            />
            <div className="flex w-full justify-center">
                <FCTransferDirectionTabs
                    value={isDirectionControlled ? direction : undefined}
                    defaultValue={isDirectionControlled ? undefined : direction}
                    onValueChange={handleDirectionChange}
                />
            </div>
        </div>
    );
}
